import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GameScorekeeper } from "@/components/GameScorekeeper";
import { Basketball, ArrowLeft, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut } from "@phosphor-icons/react";

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 1.8;
const ZOOM_STEP = 0.1;

export default function ScorekeeperGame() {
  const { gameId } = useParams();
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem("sk_zoom"));
    return saved >= ZOOM_MIN && saved <= ZOOM_MAX ? saved : 1;
  });

  useEffect(() => { if (admin === false) navigate("/admin"); }, [admin, navigate]);

  const setZoomClamped = useCallback((v) => {
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(v * 10) / 10));
    setZoom(z);
    localStorage.setItem("sk_zoom", String(z));
  }, []);

  // Keyboard shortcuts: Ctrl/Cmd +, -, 0
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === "=" || e.key === "+") { e.preventDefault(); setZoomClamped(zoom + ZOOM_STEP); }
      else if (e.key === "-") { e.preventDefault(); setZoomClamped(zoom - ZOOM_STEP); }
      else if (e.key === "0") { e.preventDefault(); setZoomClamped(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, setZoomClamped]);

  const loadTitle = useCallback(async () => {
    try {
      const [g, t] = await Promise.all([api.get("/games"), api.get("/teams")]);
      const game = g.data.find((x) => x.id === gameId);
      if (game) {
        const home = t.data.find((x) => x.id === game.home_team_id)?.name || "TBD";
        const away = t.data.find((x) => x.id === game.away_team_id)?.name || "TBD";
        setTitle(`${home} vs ${away}`);
      }
    } catch (error) { console.warn("Could not load score keeper header title:", error); }
  }, [gameId]);
  useEffect(() => { if (admin) loadTitle(); }, [admin, loadTitle]);

  if (!admin) return <div className="min-h-screen flex items-center justify-center text-white/50 bg-[#0A0A0A]">Loading…</div>;

  const ZoomBtn = ({ onClick, disabled, label, children, testid }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testid}
      className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 disabled:opacity-30 disabled:hover:border-white/15 disabled:hover:text-white/70 transition-all duration-200"
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="scorekeeper-game-page">
      <header className="flex items-center justify-between gap-3 px-4 sm:px-8 h-16 border-b border-white/10 sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 bg-[#FF5722] flex items-center justify-center shrink-0"><Basketball size={18} weight="fill" className="text-black" /></span>
          <span className="font-display text-xl sm:text-2xl uppercase truncate" data-testid="scorekeeper-game-title">{title || "Score Keeper"}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Zoom controls */}
          <div className="flex items-center gap-1.5 border border-white/10 px-1.5 py-1" data-testid="scorekeeper-zoom-controls">
            <ZoomBtn onClick={() => setZoomClamped(zoom - ZOOM_STEP)} disabled={zoom <= ZOOM_MIN} label="Zoom out" testid="zoom-out-btn">
              <MagnifyingGlassMinus size={18} />
            </ZoomBtn>
            <button
              onClick={() => setZoomClamped(1)}
              title="Reset zoom"
              data-testid="zoom-reset-btn"
              className="min-w-[3rem] text-center text-sm font-bold tabular-nums text-white/80 hover:text-[#FF5722] transition-colors"
            >
              {Math.round(zoom * 100)}%
            </button>
            <ZoomBtn onClick={() => setZoomClamped(zoom + ZOOM_STEP)} disabled={zoom >= ZOOM_MAX} label="Zoom in" testid="zoom-in-btn">
              <MagnifyingGlassPlus size={18} />
            </ZoomBtn>
            <ZoomBtn onClick={() => setZoomClamped(1)} disabled={zoom === 1} label="Fit (reset)" testid="zoom-fit-btn">
              <ArrowsOut size={16} />
            </ZoomBtn>
          </div>
          <button
            onClick={() => { if (window.history.length > 1 && window.opener) window.close(); else navigate("/admin/dashboard"); }}
            data-testid="scorekeeper-back-btn"
            className="flex items-center gap-2 border border-white/15 hover:border-white text-white/70 hover:text-white text-sm font-bold uppercase tracking-wide px-4 py-2 transition-all duration-200"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </header>
      <main className="px-4 sm:px-8 py-6 sm:py-10 overflow-x-auto">
        <div style={{ zoom }} data-testid="scorekeeper-zoom-area" className="max-w-6xl mx-auto transition-[zoom] duration-150">
          <GameScorekeeper gameId={gameId} />
        </div>
      </main>
    </div>
  );
}
